import PropTypes from 'prop-types';
import React, { Fragment, useMemo, useState } from 'react';
import moment from 'moment';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import { List, Map } from 'immutable';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { roundDownSmallValue } from '@xanadu/ui-shared/utils/numbers';
import { getMobileOS } from '@xanadu/ui-shared/utils/deviceDetection';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';

import { APP_STORE_URLS, IRELAND_COUNTRY_CODE, MOBILE_BREAKPOINT, PRODUCTS } from '../../../constants';

import ManualBonus from '../../../containers/promotions/ManualBonus';
import Currency from '../../../containers/Currency';
import PromoBanner from '../../../containers/account/PromoBanner';
import AccountLink from '../AccountLink';
import Panel from '../Panel';
import Wallet from '../Wallet';
import CasinoBonus from '../CasinoBonus';
import panelStyles from '../Panel/Panel.module.scss';

import IconCashier from '../../../../images/icons/misc/icon-cashier.svg';

import styles from './Account.module.scss';
import '../../../stylesheets/slide-transition.scss';

const OS_SYSTEM = getMobileOS();

const menuItems = {
  'bonus-details': {
    c: ManualBonus,
    title: 'Free Bet Breakdown',
  },
};

const AccountButton = ({ className = '', title, onClick = () => { } }) => (
  <span
    className={className}
    role="button"
    onClick={onClick}
  >
    {title}
  </span>
);

const Account = (props) => {
  const {
    bonuses,
    product,
    depositRequest,
    edgeStatus,
    balance,
    balanceLoading,
    updateRightSidebar,
    withdrawRequest,
    displayModal,
    lastLogin,
    widthSize,
    logout,
    title,
    casinoPromotions,
    toggleCustomerOnboardingPopup,
    betBuilderRestricted,
    countryId
  } = props;

  const [panel, setPanel] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [parentTransition, setParentTransition] = useState(true);

  const isMobile = widthSize <= MOBILE_BREAKPOINT;
  const isIrishCustomer = countryId === IRELAND_COUNTRY_CODE;

  const promotionsPageUrl =
    [PRODUCTS.exchange, PRODUCTS.mbzero].includes(product) ? '/promotions/exchange' : '/promotions/casino';

  const activeBonus = casinoPromotions.find(
    (casinoPromotion) => casinoPromotion.status === 'active'
  );

  const totalAmount = bonuses && bonuses.reduce((acc, item) => acc + item.amount, 0);

  const transitionName = parentTransition
    ? 'slide-panel-transition'
    : 'reverse-slide-panel-transition';

  const closeRightSidebar = () => {
    updateRightSidebar({
      content: null,
      visible: false
    });
  };

  const handleAccountDetailsViews = (panelUpdate) => {
    setParentTransition(false);
    setPanel(panelUpdate);
  };

  const handleDepositWithdrawButton = (type = 'deposit') => {
    closeRightSidebar();
    if (type === 'deposit') {
      depositRequest();
      track('Cashier', 'Open Deposit Modal', {
        description: 'account'
      });
    } else {
      withdrawRequest();
    }
  };

  const panelElement = useMemo(() => {
    if (panel) {
      const {
        c: PanelChildComponent,
        title: panelTitle,
      } = menuItems[panel];

      const panelClassNames = [
        panelStyles[`panel--${panel}`],
        transitionName,
      ];

      return (
        <Panel
          title={panelTitle}
          className={panelClassNames.join(' ')}
          onBack={() => {
            setParentTransition(false);
            setPanel(null);
          }}
          key={panel}
        >
          <PanelChildComponent
            {...props}
            handleAccountDetailsViews={handleAccountDetailsViews}
          />
        </Panel>
      );
    }

    return <Fragment />;
  }, [panel]);

  const renderHead = () => {
    const timestamp = value => moment(value).format('ddd Do MMM [at] HH:mm');

    const accountClass = classNames({
      [styles.header]: true,
      [styles['header--account']]: true,
      [styles['header--account--mobile']]: isMobile,
      [styles['header--mobile']]: isMobile
    });

    const logoutClass = classNames({
      [styles.logoutLink]: true,
      [styles['logoutLink--hidden']]: loggingOut,
      [styles['logoutLink--desktop']]: widthSize > MOBILE_BREAKPOINT,
    });

    const containerLogoutSpinnerClass = classNames({
      [styles.containerLogoutSpinner]: true,
      [styles['containerLogoutSpinner--desktop']]: widthSize > MOBILE_BREAKPOINT,
      [styles['containerLogoutSpinner--mobile']]: isMobile
    });

    return (
      <div className={accountClass}>
        <span
          className={styles.title}
          data-hook="login-username"
        >{title}</span>
        <div className={styles.lastLogin}>
          <span className={styles.lastLoginLabel}>
            Last Login:
          </span>
          &nbsp;
          <span className={styles.lastLoginValue}>
            {timestamp(lastLogin)}
          </span>
        </div>
        <div className={containerLogoutSpinnerClass}>
          <Spinner
            hidden={!loggingOut}
            size="small"
            theme="light"
          />
        </div>
        <a
          role="button"
          data-hook="logout"
          className={logoutClass}
          onClick={() => {
            setLoggingOut(true);
            logout();
          }}
        >
          Log Out
        </a>
      </div>
    );
  };

  const buttonClasses = classNames({
    [styles.button]: true,
  });

  const downloadIconClass = classNames({
    [styles.downloadIcon]: true,
    [styles[`downloadIcon--${OS_SYSTEM}`]]: true
  });

  const containerClass = classNames({
    [styles.container]: true,
    [styles['container--mobile']]: isMobile
  });

  return (
    <div className={styles.main}>
      {widthSize >= MOBILE_BREAKPOINT && renderHead()}
      <div className={containerClass}>
        <PromoBanner
          onNavigate={closeRightSidebar}
        />
        <div className={`${styles.row} ${styles['row--wallet']}`} >
          <Wallet
            name=""
            wallet={balance}
            status={edgeStatus}
            loading={balanceLoading}
          />
          <div className={styles.walletButtons}>
            <Button
              className={buttonClasses}
              sizeType="regular"
              buttonType="primary"
              dataHook="account-deposit"
              onClick={() => handleDepositWithdrawButton('deposit')}
            >
              <div className={styles.arrowsContainer}>
                <IconCashier />
                <div className={styles.arrowGroup} />
                <div className={styles.arrowGroup} />
              </div>
              Deposit
            </Button>
            <Button
              className={buttonClasses}
              sizeType="regular"
              buttonType="alternative"
              onClick={() => handleDepositWithdrawButton('withdraw')}
              dataHook="account-withdraw"
            >
              <div className={`${styles.arrowsContainer} ${styles['arrowsContainer--withdraw']}`}>
                <div className={`${styles.arrowGroup} ${styles['arrowGroup--withdraw']}`} />
                <div className={`${styles.arrowGroup} ${styles['arrowGroup--withdraw']}`} />
                <IconCashier />
              </div>
              Withdraw
            </Button>
          </div>
        </div>
        {activeBonus && (
          <div className={`${styles.row} ${styles['row--wallet']}`} >
            <CasinoBonus
              wallet={activeBonus.wallet}
              displayName={activeBonus.displayName}
              expiryDate={activeBonus.expiry}
              termsAndConditions={activeBonus.termsAndConditionsUrl}
              bonusType={activeBonus.type}
            />
          </div>
        )}
        {totalAmount > 0 && (
          <div
            className={`${styles.row} ${styles['row--manualBonus']}`}
            role="button"
            tabIndex={0}
            onClick={() => {
              setPanel('bonus-details');
            }}
          >
            <div className={styles.manualBonus}>
              <span className={styles.bonusTitle}>
                Bonus Funds
              </span>
              <div className={styles.bonusInfo}>
                <span className={styles.bonusValue}>
                  <Currency value={roundDownSmallValue(totalAmount)} round="floor" />
                </span>
                <span
                  className={styles.bonusDetails}
                >
                  See Details
                </span>
              </div>
            </div>
          </div>
        )}
        <div className={`${styles.row} ${styles['row--reports']}`}>
          <span className={`${styles.rowTitle} ${styles['rowTitle--reports']}`}>
            My Account
          </span>
          <div className={styles.rowAccountPage}>
            <AccountLink
              title="General Settings"
              className={styles.rowAccountPageItem}
              href="/account/general-settings"
              updateRightSidebar={updateRightSidebar}
              trackingMessage="Go to My Account Page"
            />
            <AccountLink
              title="Safer Gambling"
              className={styles.rowAccountPageItem}
              href="/account/safer-gambling"
              updateRightSidebar={updateRightSidebar}
            />
            <AccountLink
              title="Account Verification"
              className={styles.rowAccountPageItem}
              href="/account/verification"
              updateRightSidebar={updateRightSidebar}
            />
            <AccountLink
              title="Integrations"
              className={styles.rowAccountPageItem}
              href="/account/integrations"
              updateRightSidebar={updateRightSidebar}
            />
            <AccountLink
              title="Promotions"
              className={styles.rowAccountPageItem}
              href={promotionsPageUrl}
              updateRightSidebar={updateRightSidebar}
            />
            <AccountButton
              onClick={() => {
                closeRightSidebar();
                toggleCustomerOnboardingPopup({
                  visible: true,
                  firstTimeVisitor: false
                });
              }}
              title="Take a Tour"
              className={styles.rowAccountPageItem}
            />
            {
              widthSize > 768 && (
                <span
                  role="button"
                  title="Upload Documents"
                  className={styles.rowAccountPageItem}
                  onClick={() => {
                    closeRightSidebar();

                    displayModal({
                      show: true,
                      section: !isIrishCustomer ? 'upload-documents' : 'grai-verification',
                    });
                  }}
                >
                  Upload Documents
                </span>
              )
            }
          </div>
        </div>
        <div className={`${styles.row} ${styles['row--reports']}`}>
          <span className={`${styles.rowTitle} ${styles['rowTitle--reports']}`}>Reports</span>
          <div className={styles.mobileReportsMessage}>
            Download the Matchbook App to view reports
            {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
            <a
              className={downloadIconClass}
              href={APP_STORE_URLS[OS_SYSTEM]}
            />
          </div>
          <div className={styles.rowReportsList}>
            <AccountLink
              title="Unmatched"
              className={styles.rowReportsListItem}
              href="/reports/current/offers"
              updateRightSidebar={updateRightSidebar}
            />
            <AccountLink
              title="Matched"
              className={styles.rowReportsListItem}
              href="/reports/current/bets"
              updateRightSidebar={updateRightSidebar}
            />
            <AccountLink
              title="Settled"
              className={styles.rowReportsListItem}
              href="/reports/settled/bets"
              updateRightSidebar={updateRightSidebar}
            />
            <AccountLink
              title="Multiples"
              className={styles.rowReportsListItem}
              href="/reports/multiples"
              updateRightSidebar={updateRightSidebar}
            />
            {
              !betBuilderRestricted && (
                <AccountLink
                  title="Bet Builder"
                  className={styles.rowReportsListItem}
                  href="/reports/bet-builder"
                  updateRightSidebar={updateRightSidebar}
                />
              )
            }
            <AccountLink
              title="Transactions"
              className={styles.rowReportsListItem}
              href="/reports/transaction-history"
              updateRightSidebar={updateRightSidebar}
            />
            <AccountLink
              title="Account Activity"
              className={styles.rowReportsListItem}
              href="/reports/account-activity"
              updateRightSidebar={updateRightSidebar}
            />
          </div>
        </div>
        {isMobile && renderHead()}
      </div>
      <TransitionGroup
        childFactory={child => React.cloneElement(child, { classNames: transitionName })}
      >
        <CSSTransition
          classNames={transitionName}
          timeout={{
            enter: 350,
            exit: 350,
          }}
        >
          {panelElement}
        </CSSTransition>
      </TransitionGroup>
    </div >
  );
};

Account.propTypes = {
  product: PropTypes.string.isRequired,
  depositRequest: PropTypes.func.isRequired,
  edgeStatus: PropTypes.number.isRequired,
  balance: PropTypes.object.isRequired,
  balanceLoading: PropTypes.bool.isRequired,
  betBuilderRestricted: PropTypes.bool.isRequired,
  updateRightSidebar: PropTypes.func.isRequired,
  withdrawRequest: PropTypes.func.isRequired,
  displayModal: PropTypes.func.isRequired,
  lastLogin: PropTypes.string,
  widthSize: PropTypes.number.isRequired,
  logout: PropTypes.func,
  toggleCustomerOnboardingPopup: PropTypes.func,
  title: PropTypes.string,
  casinoPromotions: PropTypes.instanceOf(Map).isRequired,
  bonuses: PropTypes.instanceOf(List),
  countryId: PropTypes.number.isRequired,
};

AccountButton.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default Account;
